import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Square, Pen, Eraser, Grid3X3, Trash2, Save, ChevronLeft,
  Undo2, DoorOpen, Hand, MousePointer2, Minus, ArrowUpFromLine,
  Trees, Hammer, Grid2X2, Paintbrush, RotateCw, Stamp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOLS = [
  { key: "select",  label: "Select",   icon: MousePointer2 },
  { key: "pan",     label: "Pan",      icon: Hand },
  { key: "wall",    label: "Wall",     icon: Pen },
  { key: "room",    label: "Room",     icon: Square },
  { key: "floor",   label: "Floor",    icon: Paintbrush },
  { key: "stamp",   label: "Stamp",    icon: Stamp },
  { key: "door",    label: "Door",     icon: DoorOpen },
  { key: "window",  label: "Window",   icon: Minus },
  { key: "archway", label: "Archway",  icon: ArrowUpFromLine },
  { key: "erase",   label: "Eraser",   icon: Eraser },
];

const TEXTURES = [
  { key: "stone",   label: "Stone",   icon: Hammer },
  { key: "wood",    label: "Wood",    icon: Trees },
  { key: "checker", label: "Checker", icon: Grid2X2 },
  { key: "solid",   label: "Solid",   icon: Square },
];

// Pre-made room stamps. Each stamp is centred at (0,0) and consists of relative shapes.
// On placement, every shape is offset by the click position (snapped if Snap is on).
const STAMPS = [
  {
    key: "small-square",
    label: "Small Square Room",
    width: 200, height: 200,
    shapes: [
      { type: "room", x: -100, y: -100, w: 200, h: 200, texture: "stone" },
      { type: "door", x: 0, y: 100, rotation: 0 },
    ],
  },
  {
    key: "medium-hall",
    label: "Medium Hall",
    width: 400, height: 200,
    shapes: [
      { type: "room", x: -200, y: -100, w: 400, h: 200, texture: "wood" },
      { type: "door", x: -200, y: 0, rotation: 90 },
      { type: "door", x: 200, y: 0, rotation: 90 },
    ],
  },
  {
    key: "two-rooms",
    label: "Two-Room w/ Door",
    width: 400, height: 200,
    shapes: [
      { type: "room", x: -200, y: -100, w: 200, h: 200, texture: "stone" },
      { type: "room", x: 0, y: -100, w: 200, h: 200, texture: "stone" },
      { type: "door", x: 0, y: 0, rotation: 90 },
      { type: "door", x: -200, y: 0, rotation: 90 },
      { type: "door", x: 200, y: 0, rotation: 90 },
    ],
  },
  {
    key: "l-corridor",
    label: "L-Corridor",
    width: 400, height: 400,
    shapes: [
      { type: "room", x: -200, y: -200, w: 100, h: 400, texture: "stone" },
      { type: "room", x: -100, y: 100, w: 300, h: 100, texture: "stone" },
      { type: "door", x: -150, y: -200, rotation: 0 },
      { type: "door", x: 200, y: 150, rotation: 90 },
    ],
  },
  {
    key: "t-junction",
    label: "T-Junction",
    width: 400, height: 400,
    shapes: [
      { type: "room", x: -50, y: -200, w: 100, h: 300, texture: "stone" },
      { type: "room", x: -200, y: 100, w: 400, h: 100, texture: "stone" },
      { type: "door", x: 0, y: -200, rotation: 0 },
      { type: "door", x: -200, y: 150, rotation: 90 },
      { type: "door", x: 200, y: 150, rotation: 90 },
    ],
  },
  {
    key: "tavern",
    label: "Tavern Hall",
    width: 500, height: 400,
    shapes: [
      { type: "room", x: -250, y: -200, w: 500, h: 400, texture: "wood" },
      { type: "archway", x: 0, y: -200, rotation: 0 },
      { type: "window", x: -250, y: -100, rotation: 90 },
      { type: "window", x: -250, y: 100, rotation: 90 },
      { type: "window", x: 250, y: -100, rotation: 90 },
      { type: "window", x: 250, y: 100, rotation: 90 },
      { type: "door", x: 250, y: 0, rotation: 90 },
    ],
  },
  {
    key: "tower",
    label: "Tower (Square)",
    width: 240, height: 240,
    shapes: [
      { type: "room", x: -120, y: -120, w: 240, h: 240, texture: "checker" },
      { type: "door", x: 0, y: 120, rotation: 0 },
      { type: "window", x: -120, y: 0, rotation: 90 },
      { type: "window", x: 120, y: 0, rotation: 90 },
      { type: "window", x: 0, y: -120, rotation: 0 },
    ],
  },
];

const CANVAS_W = 2400;
const CANVAS_H = 1800;
const GRID = 50;

// Door/window/archway sizes (local coords, before rotation)
const SIZES = {
  door:    { w: 44, h: 14 },
  window:  { w: 44, h: 10 },
  archway: { w: 70, h: 18 },
};

export const MapDrawerPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const svgRef = useRef(null);
  const canvasRef = useRef(null);

  const [tool, setTool] = useState("wall");
  const [snap, setSnap] = useState(true);
  const [name, setName] = useState("Drawn Map");
  const [saving, setSaving] = useState(false);
  const [activeTexture, setActiveTexture] = useState("stone");
  const [activeStamp, setActiveStamp] = useState(STAMPS[0].key);

  // Shapes
  const [shapes, setShapes] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeWall, setActiveWall] = useState(null);
  const [activeRoom, setActiveRoom] = useState(null);
  const [hoverPos, setHoverPos] = useState(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const dragRef = useRef(null); // { id, offsetX, offsetY } or { rotating: true, id, cx, cy, startAngle, startRot }
  const isPanningRef = useRef(false);
  const panStart = useRef({ x: 0, y: 0, px: 0, py: 0 });

  const snapPoint = (p) => snap ? { x: Math.round(p.x / GRID) * GRID, y: Math.round(p.y / GRID) * GRID } : p;

  const getWorldPos = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const sx = (e.clientX - rect.left - pan.x) / zoom;
    const sy = (e.clientY - rect.top - pan.y) / zoom;
    return { x: sx, y: sy };
  };

  const getSnappedWorldPos = (e) => snapPoint(getWorldPos(e));

  const pushHistory = () => setHistory(h => [...h.slice(-50), shapes]);
  const undo = () => {
    if (history.length === 0) return;
    setShapes(history[history.length - 1]);
    setHistory(h => h.slice(0, -1));
    setSelectedId(null);
  };

  const selectedShape = useMemo(
    () => shapes.find(s => s.id === selectedId) || null,
    [shapes, selectedId]
  );

  const handlePointerDown = (e) => {
    // Middle / right click → pan
    if (e.button === 1 || e.button === 2) {
      isPanningRef.current = true;
      panStart.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
      return;
    }
    if (tool === "pan") {
      isPanningRef.current = true;
      panStart.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
      return;
    }
    const rawPos = getWorldPos(e);
    const pos = snapPoint(rawPos);

    if (tool === "select") {
      // Hit-test top-most shape
      const hit = [...shapes].reverse().find(sh => isInsideShape(sh, rawPos));
      if (hit) {
        setSelectedId(hit.id);
        if (hit.type === "door" || hit.type === "window" || hit.type === "archway") {
          dragRef.current = { id: hit.id, kind: "move", offsetX: rawPos.x - hit.x, offsetY: rawPos.y - hit.y };
        } else if (hit.type === "room") {
          dragRef.current = { id: hit.id, kind: "move-room", offsetX: rawPos.x - hit.x, offsetY: rawPos.y - hit.y };
        }
      } else {
        setSelectedId(null);
      }
      return;
    }
    if (tool === "wall") {
      if (activeWall) {
        setActiveWall(p => ({ ...p, points: [...p.points, [pos.x, pos.y]] }));
      } else {
        setActiveWall({ id: crypto.randomUUID(), points: [[pos.x, pos.y]] });
      }
    } else if (tool === "room") {
      setActiveRoom({ x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y });
    } else if (tool === "door" || tool === "window" || tool === "archway") {
      pushHistory();
      const newShape = { id: crypto.randomUUID(), type: tool, x: pos.x, y: pos.y, rotation: 0 };
      setShapes(s => [...s, newShape]);
      setSelectedId(newShape.id);
    } else if (tool === "floor") {
      // Apply active texture to room under cursor
      const hit = [...shapes].reverse().find(sh => sh.type === "room" && isInsideShape(sh, rawPos));
      if (hit) {
        pushHistory();
        setShapes(s => s.map(x => x.id === hit.id ? { ...x, texture: activeTexture } : x));
      }
    } else if (tool === "stamp") {
      const stamp = STAMPS.find(st => st.key === activeStamp);
      if (!stamp) return;
      pushHistory();
      const newShapes = stamp.shapes.map(sh => {
        const cloned = { ...sh, id: crypto.randomUUID() };
        if (cloned.type === "room") { cloned.x = pos.x + sh.x; cloned.y = pos.y + sh.y; }
        else { cloned.x = pos.x + sh.x; cloned.y = pos.y + sh.y; cloned.rotation = sh.rotation || 0; }
        return cloned;
      });
      setShapes(s => [...s, ...newShapes]);
      toast.success(`Placed: ${stamp.label}`, { duration: 1200 });
    } else if (tool === "erase") {
      const hit = [...shapes].reverse().find(sh => isInsideShape(sh, rawPos));
      if (hit) { pushHistory(); setShapes(s => s.filter(x => x.id !== hit.id)); setSelectedId(null); }
    }
  };

  const handlePointerMove = (e) => {
    if (isPanningRef.current) {
      setPan({ x: panStart.current.px + (e.clientX - panStart.current.x), y: panStart.current.py + (e.clientY - panStart.current.y) });
      return;
    }
    const rawPos = getWorldPos(e);
    setHoverPos(snapPoint(rawPos));

    // Drag/rotate selected
    if (dragRef.current) {
      const d = dragRef.current;
      if (d.kind === "rotate") {
        const angle = Math.atan2(rawPos.y - d.cy, rawPos.x - d.cx) * 180 / Math.PI;
        let rot = angle - d.startAngle + d.startRot;
        if (snap) rot = Math.round(rot / 15) * 15;
        setShapes(s => s.map(x => x.id === d.id ? { ...x, rotation: rot } : x));
      } else if (d.kind === "move") {
        const target = snap ? snapPoint({ x: rawPos.x - d.offsetX, y: rawPos.y - d.offsetY }) : { x: rawPos.x - d.offsetX, y: rawPos.y - d.offsetY };
        setShapes(s => s.map(x => x.id === d.id ? { ...x, x: target.x, y: target.y } : x));
      } else if (d.kind === "move-room") {
        const target = snap ? snapPoint({ x: rawPos.x - d.offsetX, y: rawPos.y - d.offsetY }) : { x: rawPos.x - d.offsetX, y: rawPos.y - d.offsetY };
        setShapes(s => s.map(x => x.id === d.id ? { ...x, x: target.x, y: target.y } : x));
      }
      return;
    }

    if (activeRoom) {
      const p = snapPoint(rawPos);
      setActiveRoom(r => r ? { ...r, x2: p.x, y2: p.y } : r);
    }
  };

  const handlePointerUp = () => {
    if (dragRef.current) {
      // Commit a single history entry per drag
      // (we already pushed before drag start? No — push now to allow undo)
      dragRef.current = null;
    }
    isPanningRef.current = false;
    if (activeRoom) {
      const x = Math.min(activeRoom.x1, activeRoom.x2);
      const y = Math.min(activeRoom.y1, activeRoom.y2);
      const w = Math.abs(activeRoom.x2 - activeRoom.x1);
      const h = Math.abs(activeRoom.y2 - activeRoom.y1);
      if (w > 10 && h > 10) {
        pushHistory();
        setShapes(s => [...s, { id: crypto.randomUUID(), type: "room", x, y, w, h, texture: activeTexture }]);
      }
      setActiveRoom(null);
    }
  };

  // Push history right before a drag begins
  const beginDragHistory = () => pushHistory();

  const handleDoubleClick = () => {
    if (activeWall && activeWall.points.length >= 2) {
      pushHistory();
      setShapes(s => [...s, { id: crypto.randomUUID(), type: "wall", points: activeWall.points }]);
      setActiveWall(null);
    }
  };

  const rotateSelected = (delta) => {
    if (!selectedId) return;
    setShapes(s => s.map(x => x.id === selectedId && (x.type === "door" || x.type === "window" || x.type === "archway")
      ? { ...x, rotation: (x.rotation || 0) + delta }
      : x
    ));
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "Escape") { setActiveWall(null); setActiveRoom(null); setSelectedId(null); }
      if (e.key === "Enter" && activeWall && activeWall.points.length >= 2) {
        pushHistory();
        setShapes(s => [...s, { id: crypto.randomUUID(), type: "wall", points: activeWall.points }]);
        setActiveWall(null);
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        pushHistory();
        setShapes(s => s.filter(x => x.id !== selectedId));
        setSelectedId(null);
      }
      if (e.key === "[") { e.preventDefault(); rotateSelected(-15); }
      if (e.key === "]") { e.preventDefault(); rotateSelected(15); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWall, history, shapes, selectedId]);

  const handleWheel = (e) => {
    e.preventDefault();
    // If select tool & a rotatable shape is selected, scroll rotates it
    if (tool === "select" && selectedShape && (selectedShape.type === "door" || selectedShape.type === "window" || selectedShape.type === "archway")) {
      rotateSelected(e.deltaY < 0 ? -15 : 15);
      return;
    }
    const f = e.deltaY < 0 ? 1.1 : 0.9;
    const newZoom = Math.max(0.25, Math.min(3, zoom * f));
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) { setZoom(newZoom); return; }
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    setPan({ x: mx - (mx - pan.x) * (newZoom / zoom), y: my - (my - pan.y) * (newZoom / zoom) });
    setZoom(newZoom);
  };

  // Hit detection — handles rotated rect for doors/windows/archways
  const isInsideShape = (sh, pos) => {
    if (sh.type === "room") return pos.x >= sh.x && pos.x <= sh.x + sh.w && pos.y >= sh.y && pos.y <= sh.y + sh.h;
    if (sh.type === "door" || sh.type === "window" || sh.type === "archway") {
      const { w, h } = SIZES[sh.type];
      const rad = (-(sh.rotation || 0) * Math.PI) / 180;
      const dx = pos.x - sh.x, dy = pos.y - sh.y;
      const localX = Math.cos(rad) * dx - Math.sin(rad) * dy;
      const localY = Math.sin(rad) * dx + Math.cos(rad) * dy;
      const hx = w / 2 + 6, hy = h / 2 + 6; // 6px padding for easier clicks
      return Math.abs(localX) <= hx && Math.abs(localY) <= hy;
    }
    if (sh.type === "wall") {
      for (let i = 0; i < sh.points.length - 1; i++) {
        const [ax, ay] = sh.points[i], [bx, by] = sh.points[i + 1];
        if (distToSegment(pos, ax, ay, bx, by) < 12) return true;
      }
      return false;
    }
    return false;
  };

  const distToSegment = (p, ax, ay, bx, by) => {
    const dx = bx - ax, dy = by - ay;
    if (dx === 0 && dy === 0) return Math.hypot(p.x - ax, p.y - ay);
    const t = Math.max(0, Math.min(1, ((p.x - ax) * dx + (p.y - ay) * dy) / (dx * dx + dy * dy)));
    return Math.hypot(p.x - (ax + t * dx), p.y - (ay + t * dy));
  };

  // ---- SAVE ----
  const saveMap = async () => {
    if (shapes.length === 0) { toast.error("Draw something first"); return; }
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      shapes.forEach(sh => {
        if (sh.type === "room") {
          minX = Math.min(minX, sh.x); minY = Math.min(minY, sh.y);
          maxX = Math.max(maxX, sh.x + sh.w); maxY = Math.max(maxY, sh.y + sh.h);
        } else if (sh.type === "door" || sh.type === "window" || sh.type === "archway") {
          const { w, h } = SIZES[sh.type];
          const r = Math.max(w, h);
          minX = Math.min(minX, sh.x - r); minY = Math.min(minY, sh.y - r);
          maxX = Math.max(maxX, sh.x + r); maxY = Math.max(maxY, sh.y + r);
        } else if (sh.type === "wall") {
          sh.points.forEach(([x, y]) => {
            minX = Math.min(minX, x); minY = Math.min(minY, y);
            maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
          });
        }
      });
      const pad = 60;
      minX -= pad; minY -= pad; maxX += pad; maxY += pad;
      const w = Math.max(400, maxX - minX);
      const h = Math.max(300, maxY - minY);
      const ox = -minX, oy = -minY;

      const parts = [];
      parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`);
      // Defs: floor textures
      parts.push(`<defs>`);
      parts.push(`<pattern id="tex-stone" width="80" height="80" patternUnits="userSpaceOnUse">
        <rect width="80" height="80" fill="#3d322a"/>
        <path d="M0 0 L80 0 M0 40 L80 40 M0 0 L0 80 M40 0 L40 40 M20 40 L20 80 M60 40 L60 80" stroke="#26201a" stroke-width="2"/>
      </pattern>`);
      parts.push(`<pattern id="tex-wood" width="160" height="40" patternUnits="userSpaceOnUse">
        <rect width="160" height="40" fill="#5a3f25"/>
        <path d="M0 0 L160 0 M0 20 L160 20 M0 40 L160 40" stroke="#3a2814" stroke-width="2"/>
        <path d="M40 0 L40 20 M120 0 L120 20 M80 20 L80 40" stroke="#3a2814" stroke-width="2"/>
      </pattern>`);
      parts.push(`<pattern id="tex-checker" width="80" height="80" patternUnits="userSpaceOnUse">
        <rect width="80" height="80" fill="#3d322a"/>
        <rect x="0" y="0" width="40" height="40" fill="#4a3d2f"/>
        <rect x="40" y="40" width="40" height="40" fill="#4a3d2f"/>
      </pattern>`);
      parts.push(`</defs>`);
      parts.push(`<rect width="100%" height="100%" fill="#1f1b16"/>`);
      // grid
      parts.push(`<g stroke="#2a241d" stroke-width="1">`);
      for (let x = 0; x < w; x += GRID) parts.push(`<line x1="${x}" y1="0" x2="${x}" y2="${h}"/>`);
      for (let y = 0; y < h; y += GRID) parts.push(`<line x1="0" y1="${y}" x2="${w}" y2="${y}"/>`);
      parts.push(`</g>`);
      // floors
      shapes.filter(s => s.type === "room").forEach(s => {
        const fill = s.texture && s.texture !== "solid" ? `url(#tex-${s.texture})` : "#3d322a";
        parts.push(`<rect x="${s.x + ox}" y="${s.y + oy}" width="${s.w}" height="${s.h}" fill="${fill}" stroke="#1a1310" stroke-width="6"/>`);
      });
      // walls
      shapes.filter(s => s.type === "wall").forEach(s => {
        const d = s.points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0] + ox} ${p[1] + oy}`).join(" ");
        parts.push(`<path d="${d}" fill="none" stroke="#0d0a07" stroke-width="14" stroke-linecap="round" stroke-linejoin="round"/>`);
      });
      // doors / windows / archways
      shapes.filter(s => s.type === "door" || s.type === "window" || s.type === "archway").forEach(s => {
        parts.push(`<g transform="translate(${s.x + ox} ${s.y + oy}) rotate(${s.rotation || 0})">`);
        if (s.type === "door") {
          parts.push(`<rect x="-22" y="-7" width="44" height="14" fill="#5a4836" stroke="#1a1310" stroke-width="2" rx="2"/>`);
          parts.push(`<circle cx="14" cy="0" r="3" fill="#c9a26b"/>`);
        } else if (s.type === "window") {
          parts.push(`<rect x="-22" y="-5" width="44" height="10" fill="#7aa6c0" stroke="#1a1310" stroke-width="2"/>`);
          parts.push(`<line x1="-22" y1="0" x2="22" y2="0" stroke="#1a1310" stroke-width="1.5"/>`);
          parts.push(`<line x1="0" y1="-5" x2="0" y2="5" stroke="#1a1310" stroke-width="1.5"/>`);
        } else if (s.type === "archway") {
          parts.push(`<path d="M -35 9 L -35 -9 A 35 14 0 0 1 35 -9 L 35 9 Z" fill="none" stroke="#0d0a07" stroke-width="6"/>`);
          parts.push(`<path d="M -29 9 L -29 -7 A 29 12 0 0 1 29 -7 L 29 9 Z" fill="#1f1b16" stroke="none"/>`);
        }
        parts.push(`</g>`);
      });
      parts.push(`</svg>`);
      const svgStr = parts.join("");

      const blob = await svgToPngBlob(svgStr, w, h);
      const createRes = await axios.post(`${API}/maps`, {
        campaign_id: campaignId,
        name: name.trim(),
        description: "Drawn with the in-app Map Drawer",
        location_id: null,
        type: "world",
      }, { withCredentials: true });
      const mapId = createRes.data.id;
      const fd = new FormData();
      fd.append("file", new File([blob], `${name.replace(/[^a-z0-9]/gi, "_")}.png`, { type: "image/png" }));
      await axios.post(`${API}/maps/${mapId}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`"${name}" saved to your Maps`);
      navigate(`/campaign/${campaignId}/maps`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Begin drag history snapshot when pointer goes down on a draggable
  // (we attach inline via onPointerDown handlers on the shapes themselves to push once)
  const onShapePointerDown = (e, sh) => {
    if (tool !== "select") return;
    e.stopPropagation();
    setSelectedId(sh.id);
    beginDragHistory();
    const rawPos = getWorldPos(e);
    if (sh.type === "room") {
      dragRef.current = { id: sh.id, kind: "move-room", offsetX: rawPos.x - sh.x, offsetY: rawPos.y - sh.y };
    } else {
      dragRef.current = { id: sh.id, kind: "move", offsetX: rawPos.x - sh.x, offsetY: rawPos.y - sh.y };
    }
  };

  const onRotateHandlePointerDown = (e, sh) => {
    e.stopPropagation();
    beginDragHistory();
    const rawPos = getWorldPos(e);
    const startAngle = Math.atan2(rawPos.y - sh.y, rawPos.x - sh.x) * 180 / Math.PI;
    dragRef.current = { id: sh.id, kind: "rotate", cx: sh.x, cy: sh.y, startAngle, startRot: sh.rotation || 0 };
  };

  const cursor = (() => {
    if (isPanningRef.current || tool === "pan") return "grab";
    if (tool === "erase") return "not-allowed";
    if (tool === "select") return "default";
    if (tool === "floor") return "cell";
    return "crosshair";
  })();

  return (
    <div className="fixed inset-0 bg-[#0f0d0a] flex flex-col z-50" data-testid="map-drawer">
      {/* Top bar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-[#2a241d] bg-[#18140f] flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="drawer-back">
          <ChevronLeft className="w-4 h-4 mr-1" /> Cancel
        </Button>
        <div className="h-5 w-px bg-[#2a241d]" />
        {TOOLS.map(t => {
          const Icon = t.icon;
          const active = tool === t.key;
          return (
            <Button
              key={t.key}
              variant={active ? "default" : "ghost"}
              size="sm"
              onClick={() => { setTool(t.key); setActiveWall(null); setActiveRoom(null); if (t.key !== "select") setSelectedId(null); }}
              className={active ? "bg-amber-500 hover:bg-amber-600 text-black" : "text-[#bbb]"}
              data-testid={`tool-${t.key}`}
              title={t.label}
            >
              <Icon className="w-4 h-4 mr-1" /> {t.label}
            </Button>
          );
        })}
        <div className="h-5 w-px bg-[#2a241d]" />
        {/* Floor texture picker */}
        <div className="flex items-center gap-1" data-testid="texture-picker">
          {TEXTURES.map(tx => {
            const Icon = tx.icon;
            const active = activeTexture === tx.key;
            return (
              <button
                key={tx.key}
                onClick={() => setActiveTexture(tx.key)}
                className={`text-[10px] px-2 py-1 rounded-sm flex items-center gap-1 border ${active ? "bg-amber-500/20 text-amber-300 border-amber-500/50" : "text-[#888] border-[#2a241d] hover:text-[#ccc]"}`}
                title={`Floor: ${tx.label}`}
                data-testid={`texture-${tx.key}`}
              >
                <Icon className="w-3 h-3" /> {tx.label}
              </button>
            );
          })}
        </div>
        {/* Stamp picker — only visible when Stamp tool is active */}
        {tool === "stamp" && (
          <>
            <div className="h-5 w-px bg-[#2a241d]" />
            <select
              value={activeStamp}
              onChange={(e) => setActiveStamp(e.target.value)}
              className="text-[10px] px-2 py-1 rounded-sm bg-[#0f0d0a] border border-amber-500/40 text-amber-300 focus:outline-none focus:border-amber-500"
              data-testid="stamp-picker"
              title="Pre-made room layout"
            >
              {STAMPS.map(st => <option key={st.key} value={st.key}>{st.label} ({Math.round(st.width / GRID)}×{Math.round(st.height / GRID)})</option>)}
            </select>
          </>
        )}
        <div className="h-5 w-px bg-[#2a241d]" />
        <Button variant="ghost" size="sm" onClick={undo} disabled={history.length === 0} className="text-[#bbb]" data-testid="drawer-undo">
          <Undo2 className="w-4 h-4 mr-1" /> Undo
        </Button>
        <Button variant="ghost" size="sm" onClick={() => { if (shapes.length && confirm("Clear all?")) { pushHistory(); setShapes([]); setSelectedId(null); } }} className="text-red-400 hover:text-red-300" data-testid="drawer-clear">
          <Trash2 className="w-4 h-4 mr-1" /> Clear
        </Button>
        <button
          onClick={() => setSnap(s => !s)}
          className={`text-xs px-2 py-1 rounded-sm flex items-center gap-1 ${snap ? "bg-amber-500/15 text-amber-400 border border-amber-500/40" : "text-[#888] border border-[#2a241d]"}`}
          data-testid="drawer-snap"
        >
          <Grid3X3 className="w-3.5 h-3.5" /> Snap {snap ? "On" : "Off"}
        </button>
        <div className="flex-1" />
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Map name"
          className="w-40 h-8 bg-[#0f0d0a] border-[#2a241d] text-[#eee]"
          data-testid="drawer-name"
        />
        <Button onClick={saveMap} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="drawer-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save Map"}
        </Button>
      </div>

      {/* Hint */}
      <div className="px-4 py-1.5 bg-[#0f0d0a] border-b border-[#2a241d] text-[10px] text-[#888]">
        {tool === "select"  && (selectedShape ? "Drag to move · Scroll or [ ] to rotate · Del to delete · Esc to deselect" : "Click any shape to select it")}
        {tool === "wall"    && (activeWall ? "Click to add wall points · Double-click or Enter to finish · Esc to cancel" : "Click to start a wall")}
        {tool === "room"    && `Click and drag to draw a room (texture: ${activeTexture})`}
        {tool === "floor"   && `Click any room to apply ${activeTexture} texture`}
        {tool === "stamp"   && `Click anywhere to drop a "${STAMPS.find(s => s.key === activeStamp)?.label}" room`}
        {tool === "door"    && "Click anywhere to place a door"}
        {tool === "window"  && "Click anywhere to place a window"}
        {tool === "archway" && "Click anywhere to place an archway"}
        {tool === "erase"   && "Click any shape to delete it"}
        {tool === "pan"     && "Click and drag to pan"}
        {" · Scroll to zoom · Ctrl+Z to undo"}
      </div>

      {/* Canvas */}
      <div ref={canvasRef} className="flex-1 relative overflow-hidden" style={{ cursor }}>
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onDoubleClick={handleDoubleClick}
          onWheel={handleWheel}
          onContextMenu={e => e.preventDefault()}
          style={{ background: "#1f1b16", touchAction: "none" }}
        >
          <defs>
            <pattern id="tex-stone" width="80" height="80" patternUnits="userSpaceOnUse">
              <rect width="80" height="80" fill="#3d322a" />
              <path d="M0 0 L80 0 M0 40 L80 40 M0 0 L0 80 M40 0 L40 40 M20 40 L20 80 M60 40 L60 80" stroke="#26201a" strokeWidth="2" />
            </pattern>
            <pattern id="tex-wood" width="160" height="40" patternUnits="userSpaceOnUse">
              <rect width="160" height="40" fill="#5a3f25" />
              <path d="M0 0 L160 0 M0 20 L160 20 M0 40 L160 40" stroke="#3a2814" strokeWidth="2" />
              <path d="M40 0 L40 20 M120 0 L120 20 M80 20 L80 40" stroke="#3a2814" strokeWidth="2" />
            </pattern>
            <pattern id="tex-checker" width="80" height="80" patternUnits="userSpaceOnUse">
              <rect width="80" height="80" fill="#3d322a" />
              <rect x="0" y="0" width="40" height="40" fill="#4a3d2f" />
              <rect x="40" y="40" width="40" height="40" fill="#4a3d2f" />
            </pattern>
          </defs>

          <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
            {/* Grid */}
            <g stroke="#2a241d" strokeWidth={1 / zoom}>
              {Array.from({ length: Math.floor(CANVAS_W / GRID) }, (_, i) => (
                <line key={`v${i}`} x1={i * GRID} y1={0} x2={i * GRID} y2={CANVAS_H} />
              ))}
              {Array.from({ length: Math.floor(CANVAS_H / GRID) }, (_, i) => (
                <line key={`h${i}`} x1={0} y1={i * GRID} x2={CANVAS_W} y2={i * GRID} />
              ))}
            </g>

            {/* Rooms (floors) */}
            {shapes.filter(s => s.type === "room").map(s => {
              const fill = s.texture && s.texture !== "solid" ? `url(#tex-${s.texture})` : "#3d322a";
              return (
                <rect
                  key={s.id}
                  x={s.x} y={s.y} width={s.w} height={s.h}
                  fill={fill}
                  stroke={selectedId === s.id ? "#fbbf24" : "#1a1310"}
                  strokeWidth={selectedId === s.id ? 4 : 6}
                  onPointerDown={e => onShapePointerDown(e, s)}
                  data-testid={`shape-room-${s.id}`}
                  style={{ cursor: tool === "select" ? "move" : undefined }}
                />
              );
            })}

            {/* Active room preview */}
            {activeRoom && (() => {
              const x = Math.min(activeRoom.x1, activeRoom.x2);
              const y = Math.min(activeRoom.y1, activeRoom.y2);
              const w = Math.abs(activeRoom.x2 - activeRoom.x1);
              const h = Math.abs(activeRoom.y2 - activeRoom.y1);
              const fill = activeTexture && activeTexture !== "solid" ? `url(#tex-${activeTexture})` : "#3d322a";
              return <rect x={x} y={y} width={w} height={h} fill={fill} stroke="#fbbf24" strokeWidth="3" strokeDasharray="6 3" opacity="0.6" />;
            })()}

            {/* Walls */}
            {shapes.filter(s => s.type === "wall").map(s => (
              <path key={s.id} d={s.points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ")} fill="none" stroke="#0d0a07" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
            ))}

            {/* Active wall preview */}
            {activeWall && (
              <>
                <path d={activeWall.points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ") + (hoverPos ? ` L ${hoverPos.x} ${hoverPos.y}` : "")} fill="none" stroke="#fbbf24" strokeWidth="4" strokeDasharray="6 3" />
                {activeWall.points.map(([x, y], i) => (
                  <circle key={i} cx={x} cy={y} r="5" fill="#fbbf24" />
                ))}
              </>
            )}

            {/* Doors / windows / archways */}
            {shapes.filter(s => s.type === "door" || s.type === "window" || s.type === "archway").map(s => {
              const isSel = selectedId === s.id;
              const sz = SIZES[s.type];
              return (
                <g
                  key={s.id}
                  transform={`translate(${s.x} ${s.y}) rotate(${s.rotation || 0})`}
                  onPointerDown={e => onShapePointerDown(e, s)}
                  style={{ cursor: tool === "select" ? "move" : undefined }}
                  data-testid={`shape-${s.type}-${s.id}`}
                >
                  {s.type === "door" && (
                    <>
                      <rect x="-22" y="-7" width="44" height="14" fill="#5a4836" stroke="#1a1310" strokeWidth="2" rx="2" />
                      <circle cx="14" cy="0" r="3" fill="#c9a26b" />
                    </>
                  )}
                  {s.type === "window" && (
                    <>
                      <rect x="-22" y="-5" width="44" height="10" fill="#7aa6c0" stroke="#1a1310" strokeWidth="2" />
                      <line x1="-22" y1="0" x2="22" y2="0" stroke="#1a1310" strokeWidth="1.5" />
                      <line x1="0" y1="-5" x2="0" y2="5" stroke="#1a1310" strokeWidth="1.5" />
                    </>
                  )}
                  {s.type === "archway" && (
                    <>
                      <path d="M -35 9 L -35 -9 A 35 14 0 0 1 35 -9 L 35 9 Z" fill="none" stroke="#0d0a07" strokeWidth="6" />
                      <path d="M -29 9 L -29 -7 A 29 12 0 0 1 29 -7 L 29 9 Z" fill="#1f1b16" stroke="none" />
                    </>
                  )}
                  {/* Selection outline */}
                  {isSel && (
                    <>
                      <rect
                        x={-(sz.w / 2 + 4)} y={-(sz.h / 2 + 4)}
                        width={sz.w + 8} height={sz.h + 8}
                        fill="none" stroke="#fbbf24" strokeWidth={2 / zoom} strokeDasharray="4 3"
                      />
                      {/* Rotation handle */}
                      <line x1={0} y1={-(sz.h / 2 + 4)} x2={0} y2={-(sz.h / 2 + 22)} stroke="#fbbf24" strokeWidth={2 / zoom} />
                      <circle
                        cx={0} cy={-(sz.h / 2 + 26)} r={6 / zoom}
                        fill="#fbbf24"
                        onPointerDown={e => onRotateHandlePointerDown(e, s)}
                        style={{ cursor: "grab" }}
                        data-testid={`rotate-handle-${s.id}`}
                      />
                    </>
                  )}
                </g>
              );
            })}
          </g>
        </svg>

        {/* Floating selected-info pill */}
        {selectedShape && (selectedShape.type === "door" || selectedShape.type === "window" || selectedShape.type === "archway") && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-[#18140f]/95 border border-amber-500/40 rounded-sm px-3 py-1.5 text-[11px] text-amber-300 flex items-center gap-2" data-testid="selected-info">
            <span className="capitalize">{selectedShape.type}</span>
            <span className="text-[#888]">·</span>
            <RotateCw className="w-3 h-3" /> {Math.round(((selectedShape.rotation || 0) % 360 + 360) % 360)}°
            <button onClick={() => rotateSelected(-15)} className="ml-2 px-1.5 py-0.5 rounded-sm bg-[#0f0d0a] border border-[#2a241d] hover:border-amber-500/50" title="Rotate -15° ([)">[</button>
            <button onClick={() => rotateSelected(15)}  className="px-1.5 py-0.5 rounded-sm bg-[#0f0d0a] border border-[#2a241d] hover:border-amber-500/50" title="Rotate +15° (])">]</button>
          </div>
        )}

        {/* Bottom-right counters */}
        <div className="absolute bottom-3 right-3 bg-[#18140f]/90 border border-[#2a241d] rounded-sm px-3 py-1.5 text-[10px] text-[#888] flex gap-3">
          <span data-testid="counter-shapes">Shapes: {shapes.length}</span>
          <span>Zoom: {Math.round(zoom * 100)}%</span>
        </div>
      </div>
    </div>
  );
};

async function svgToPngBlob(svgStr, w, h) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob(b => b ? resolve(b) : reject(new Error("toBlob null")), "image/png");
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export default MapDrawerPage;
